import type { TopLevelMenuItem } from '../../types';
import { Icon } from '@workramp/ui';
import { Collapse } from '~/features/common';
import { MenuItemWrapper, MenuLink, MenuList, MenuText, MenuSubtext, ExpansionButton } from './Sidebar.styles';
import { isActivePath } from './Sidebar.utils';

const { I18n } = window;

export interface SidebarMenuItemProps extends TopLevelMenuItem {
  isExpanded?: boolean;
  onExpand: (expandedHref: string | null) => void;
}

export function SidebarMenuItem({
  isExpanded,
  onExpand,
  text,
  href,
  inactiveIconName,
  activeIconName,
  subitems,
  onClick,
  rootActivePath,
}: SidebarMenuItemProps) {
  const isActive = isActivePath(href, rootActivePath);
  const hasActiveChild = subitems?.some((child) => isActivePath(child.href));

  const iconName = isActive || hasActiveChild ? activeIconName : inactiveIconName;

  const TRANSLATED_EXPAND = I18n.t('js.learner.home.sidebar.expand');
  const TRANSLATED_COLLAPSE = I18n.t('js.learner.home.sidebar.collapse');

  return (
    <MenuItemWrapper>
      <MenuLink
        href={href}
        $isActive={isActive && !hasActiveChild}
        onClick={onClick}
        aria-label={text}
        aria-selected={isActive && !hasActiveChild}
      >
        <Icon name={iconName} size="lg" />
        <MenuText size={1} weight="medium">
          {text}
        </MenuText>
      </MenuLink>

      {subitems?.length && (
        <>
          <ExpansionButton
            $isExpanded={isExpanded}
            variant="text"
            color="secondary"
            size="xl"
            label={isExpanded ? TRANSLATED_COLLAPSE : TRANSLATED_EXPAND}
            iconName="caret-down:regular"
            onPress={() => onExpand(isExpanded ? null : href)}
          />

          <Collapse isCollapsed={!isExpanded}>
            <MenuList>
              {subitems.map((child) => {
                const isItemActive = isActivePath(child.href);
                return (
                  <li key={child.text}>
                    <MenuLink
                      href={child.href}
                      $isActive={isItemActive}
                      aria-selected={isItemActive}
                      onClick={child.onClick}
                      aria-label={child.text}
                    >
                      <MenuSubtext size={1} weight="medium">
                        {child.text}
                      </MenuSubtext>
                    </MenuLink>
                  </li>
                );
              })}
            </MenuList>
          </Collapse>
        </>
      )}
    </MenuItemWrapper>
  );
}
